/* -----------------------------------------------------------------------------------------------------
  - Created: [31-01-2025] by [SREERAJ]
  - Modified: [YYYY-MM-DD]
  - Description: This file contains Technology list and modals
  -------------------------------------------------------------------------------------------------------*/
import React, { useEffect, useState } from 'react'
import './TechnologyTableStyle.css'
import CustomBottomPagination from '../../../../Components/custom_components/CustomBottomPagination/CustomBottomPagination'
import CustomTable from '../../../../Components/custom_components/CustomTable/CustomTable'
import CustomModal from '../../../../Components/custom_components/CustomModal/CustomModal'
import TechnologyForm from '../TechnologyForm/TechnologyForm'
import { ErrorToast, InfoToast, SuccessToast } from '../../../../utils/toast'
import { handlePaginationEntries } from '../../../../utils/pagination'
import TechnologySearchFilter from '../TechnologySearchFilter/TechnologySearchFilter'
import { get_technology_by_id, get_technology_with_pagination, technology_creation, technology_deletion, technology_update } from '../../../../config/Resource/technologyApis'
import { useSelector } from 'react-redux'
import { hasPrivilege } from '../../../../utils/privileges'
import Loader from '../../../../Components/Loader/Loader'
import { Icons } from '../../../../utils/GlobalIcons'
import CustomTooltipWithoutRoundBorder from '../../../../Components/CustomActionButtonWithTooltip/CustomTooltipWithoutRoundBorder'
import { getStatusColors } from '../../../../utils/constants'

function TechnologyTable() {

    const privileges = useSelector((state) => state.privileges.privileges);


    const [technologyListing, setTechnologyListing] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalItemPerPage, setTotalItemPerPage] = useState(25);
    const [totalPages, setTotalPages] = useState(0);
    const [refresh, setRefresh] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [filterValues, setFilterValues] = useState({});
    const [isLoading, setIsLoading] = useState(false); // Loader state

    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
 
    const [modalProps, setModalProps] = useState({
        isOpen: false,
        id: '',
        formValues: {}
    });

    const [pagination, setPagination] = useState({
        start: 0,
        end: 0,
        totalEntries: 0,
    });

    useEffect(() => {
        get_technologies()
    }, [pageNumber, totalItemPerPage, refresh, filterValues])


    // Handle search filter change
    const handleSearchFilter = (filterValues) => {
        setFilterValues(filterValues);
    };

    const get_technologies = async () => {
        const formData = new FormData();
        formData.append('page', pageNumber);
        formData.append('pageItemcount', totalItemPerPage);
        Object.keys(filterValues).forEach(key => formData.append(key, filterValues[key]));
        try {
            setIsLoading(true)
            const response = await get_technology_with_pagination(formData)
            if (response?.status === "success") {
                setTechnologyListing(response.data.data)
                setTotalPages(response.data.last_page)
                const updatedPagination = handlePaginationEntries(response.data.from, response.data.to, response.data.total)
                setPagination(updatedPagination);
            } else {
                setTechnologyListing([])
            }
        } catch (error) {
            console.log("error", error);
        } finally {
            setIsLoading(false)
        }
    }
    // Define the required permission actions
    const PERMISSION_ACTIONS = {
        view: 4,
        edit: 5,
        history: 6,
        add: 3,
    };

    // Map permissions to boolean values based on privileges
    const userPermissions = {
        view: hasPrivilege(privileges, "Resources", 4, PERMISSION_ACTIONS.view),
        edit: hasPrivilege(privileges, "Resources", 4, PERMISSION_ACTIONS.edit),
        history: hasPrivilege(privileges, "Resources", 4, PERMISSION_ACTIONS.history),
        add: hasPrivilege(privileges, "Resources", 4, PERMISSION_ACTIONS.add),
    };

    // / Function to check if any action-related permissions are granted
    const hasActionPermissions = userPermissions.view || userPermissions.edit || userPermissions.history;


    const columns = [
        {
            title: "#",
            key: "key",
        },
        {
            title: "Technology",
            key: "technology",
        }, {
            title: "Short Code",
            key: "technologyShortCode",
        }, {
            title: "Description",
            key: "TechnologiesDescription",
        },

        {
            title: "Status",
            key: "status",
            render: (_, record) => {
                //  actual status text
                const statusText = record?.status || "Unknown";

                const statusCode = statusText.toLowerCase() === "active" ? 1 : statusText.toLowerCase() === "inactive" ? 2 : '';
                const style = getStatusColors(statusCode);

                return (
                    <span className="status-badge" style={style}>
                        {statusText}
                    </span>
                );
            },
        },
        { title: "Actions", key: "actions", render: (text, row) => customActions(row) },
    ].filter(column => column.key !== "actions" || hasActionPermissions); //removes the "Actions" column when hasActionPermissions is false

    // Render action icons for each project item
    const customActions = (row) => (
        <div className="action-container">
            {userPermissions.view &&
                <CustomTooltipWithoutRoundBorder title="View ">
                    <Icons.View className="view-action-icon" onClick={() => handleModal(true, row?.id, 'view')} />
                </CustomTooltipWithoutRoundBorder>

            }
            {userPermissions.edit &&
                <CustomTooltipWithoutRoundBorder title="Edit">
                    <Icons.Edit className="edit-action-icon" onClick={() => handleModal(true, row?.id)} />
                </CustomTooltipWithoutRoundBorder>

            }
        </div>
    );


    const datas = () => technologyListing?.map((item, index) => ({
        key: pageNumber * totalItemPerPage - (totalItemPerPage - (index + 1)),
        id: item.TechnologiesTypeId,
        technology: item.TechnologiesName,
        technologyShortCode: item.TechnologiesShortCode,
        status: item?.technology_Status,
        statusId: item.TechnologiesTypeStatus,
        TechnologiesDescription: item?.TechnologiesDescription,
    }));


    // Open Add/Edit/View modal
    const handleModal = async (isOpen, technology_Id, actionType) => {
        if (technology_Id) {

            const response = await get_technology_by_id({ technologyID: technology_Id })
            if (response?.status === "success") {

                setModalProps({
                    isOpen: true,
                    id: response?.data?.TechnologiesTypeId,
                    formValues: {
                        TechnologiesTypeName: response?.data?.TechnologiesTypeName,
                        TechnologiesTypeStatus: response?.data?.TechnologiesTypeStatus,
                        TechnologiesShortcode: response?.data?.TechnologiesShortcode,
                        TechnologiesDescription: response?.data?.TechnologiesDescription || "",
                    }
                });

                if (actionType === 'view') {
                    setIsViewModalOpen(true);
                }
            }
        } else {
            // For 'add' case
            setModalProps({
                isOpen,
                id: '',
                formValues: {
                    TechnologiesTypeName: '',
                    TechnologiesShortcode: '',
                    TechnologiesTypeStatus: 1,
                    TechnologiesDescription: '',
                }
            });
        }
    };

    const handleTechnologySave = async (values, technologyId) => {
        const formData = new FormData();
        for (const key in values) {
            formData.append(key, values[key]);
        }

        try {
            setIsButtonDisabled(true);

            if (technologyId) {
                formData.append('technologyID', technologyId);
            }

            const apiFunction = technologyId ? technology_update : technology_creation;
            const response = await apiFunction(formData);

            const message = response?.data?.message || 'Something went wrong!';
            const status = response?.data?.status;
            if (status === "success") {
                SuccessToast(message);
                handleModal(false);
                setRefresh(!refresh);
            } else {
                ErrorToast(message);
            }
        } catch (error) {
         
        } finally {
            setIsButtonDisabled(false);
        }
    };

    const updatePagination = (pageNumber) => setPageNumber(pageNumber);
    const updateItemPageCount = (pageItemCount) => setTotalItemPerPage(pageItemCount);

    // Close modal
    const closeModal = () => {
        setModalProps({ ...modalProps, isOpen: false });
        setIsViewModalOpen(false);
    };

 
    return (
        <div className="technology-table-container">
            <TechnologySearchFilter searchFilter={handleSearchFilter} tableTitle="Technology" isAddEditModalOpen={modalProps.isOpen} handleAddEditModal={handleModal} isAddPermission={userPermissions?.add}

                on_change_value={updatePagination}
                on_page_count_update={updateItemPageCount}
            />
            <CustomTable columns={columns} rows={isLoading ? [] : datas()} isLoading={isLoading} />

            {isLoading ? <Loader /> : <CustomBottomPagination
                items_per_page={totalItemPerPage}
                current_page={pageNumber}
                total_page_count={totalPages}
                on_change_value={updatePagination}
                on_page_count_update={updateItemPageCount}
                pagination={pagination}
            />
            }
            {modalProps.isOpen && <CustomModal
                open={modalProps.isOpen}
                handleClose={closeModal}
                title={isViewModalOpen ? "View Technology" : modalProps.id ? "Edit Technology" : "Create Technology"}
                body={<TechnologyForm
                    isDisabled={isViewModalOpen}
                    isButtonDisabled={isButtonDisabled}
                    handleTechnologySave={handleTechnologySave}
                    id={modalProps.id}
                    formIntialValues={modalProps.formValues}
                />}
                maxWidth="1000px"
            />}
        </div>
    );
}

export default TechnologyTable;
